import { useState } from "react";
import { useHistory } from "react-router-dom";
import { withClientContext } from "~/apollo/client";
import { globalEnv } from "~/global-env";
import {
  useAssignDdqProdOpsTaskMutation,
  useStartDdqProdOpsTaskMutation,
} from "~/graphql/supergraphHooks";
import type { DdqQueueTask } from "../types";

export type UseProcessTaskActionsResult = {
  onSelectAssignee: (assigneeId: string) => void;
  onUnassign: () => void;
  onStart: () => void;
  isAssigning: boolean;
  isStarting: boolean;
  startError: string | null;
  assignError: string | null;
};

// Navigates into the wizard on a successful start — the real product entry point.
export const useProcessTaskActions = (
  targetId: string,
  task: DdqQueueTask | undefined,
): UseProcessTaskActionsResult => {
  const history = useHistory();
  const [startError, setStartError] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  const [assignTask, { loading: isAssigning }] = useAssignDdqProdOpsTaskMutation({
    context: withClientContext("federation"),
  });
  const [startTask, { loading: isStarting }] = useStartDdqProdOpsTaskMutation({
    context: withClientContext("federation"),
  });

  const onSelectAssignee = (assigneeId: string) => {
    if (!task) return;
    setStartError(null);
    setAssignError(null);
    assignTask({ variables: { taskId: task.id, assigneeId } }).catch((error: unknown) => {
      setAssignError(error instanceof Error ? error.message : "Something went wrong.");
    });
  };

  // Reuses assignTask — omitting assigneeId clears the assignee server-side.
  const onUnassign = () => {
    if (!task) return;
    setStartError(null);
    setAssignError(null);
    assignTask({ variables: { taskId: task.id } }).catch((error: unknown) => {
      setAssignError(error instanceof Error ? error.message : "Something went wrong.");
    });
  };

  const onStart = () => {
    if (!task) return;
    setStartError(null);
    setAssignError(null);
    startTask({ variables: { taskId: task.id } })
      .then(() => history.push(`${globalEnv.ddqIntakeRoute}/${targetId}/process`))
      .catch((error: unknown) => {
        setStartError(error instanceof Error ? error.message : "Something went wrong.");
      });
  };

  return {
    onSelectAssignee,
    onUnassign,
    onStart,
    isAssigning,
    isStarting,
    startError,
    assignError,
  };
};
